import { skipToken } from "@reduxjs/toolkit/query/react";
import { format } from "date-fns";
import { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Spinner, Typography } from "@/components/atoms";
import { isCreditCard } from "@/components/organisms/PaymentSettings/helpers";
// Type-only import — erased at runtime, so no module cycle (ClientBookingConfirmation → confirmation cards).
import type { ClientBookingConfirmationState } from "@/components/pages/Client/ClientBookingConfirmation";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import { driverDelayCaption, formatDisplayJobId } from "@/helpers/bookingHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import {
  useCreateClientBookingMutation,
  useDeleteClientBookingAttachmentMutation,
  useGetClientBookingDocumentsQuery,
  useUpdateClientBookingMutation,
  useUploadClientBookingAttachmentMutation,
} from "@/redux/apis/clientBooking/clientBookingApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { buildBookingDiff } from "@/redux/slices/clientBooking/bookingDiff";
import { buildBookingPayload, transferRateKind } from "@/redux/slices/clientBooking/bookingPayload";
import { clearBooking } from "@/redux/slices/clientBooking/clientBookingSlice";
import type { RebookSource } from "@/redux/slices/clientBooking/rebookDraft";
import {
  selectClientBookingDraft,
  selectEditJobId,
  selectEditOriginal,
  selectOrderedLocations,
  selectPaymentMethod,
} from "@/redux/slices/clientBooking/selectors";
import { addToast, clsx } from "@/utils";
import { useAccountHolder } from "../useAccountHolder";
import { useBookingAttachments } from "../BookingAttachmentsContext";
// Type-only import — erased at runtime, so no module cycle with BookingSummaryPanel.
import type { BookingStep } from "../BookingSummaryPanel";
import { BookingUpdateReviewModal } from "../BookingUpdateReviewModal";
import { useBookingViewOnly } from "../BookingViewOnlyContext";
import { ConfirmCardModal } from "../ConfirmCardModal";
import type { TripDetails } from "../confirmation/TripDetailsCard";
import { useBookingQuote } from "../hooks/useBookingQuote";
import { useSheetFlightPickup } from "../hooks/useSheetFlightPickup";
import { uploadBookingAttachments } from "../uploadBookingAttachments";
import { useAttachmentDisplayNames } from "../useAttachmentDisplayNames";
import { capacityLabel, extrasLabel, formatDuration } from "../vehicleTransfer";
import { AddressSummaryCard } from "./AddressSummaryCard";
import { GuestPricedBookingSummary } from "./GuestPricedBookingSummary";
import { PaymentSummaryCard } from "./PaymentSummaryCard";
import { QuotePriceSummary, fmt, toGroups } from "./QuotePriceSummary";
import { ScopedClientCard } from "./ScopedClientCard";
import { TripDetailsSummaryCard } from "./TripDetailsSummaryCard";
import { TrustNote } from "./TrustNote";

interface PricedBookingSummaryProps {
  className?: string;
  hideHeading?: boolean;
  /** The wizard step currently shown — its own summary card hides the edit pencil. */
  activeStep?: BookingStep;
  /** Jump back to a wizard step (the cards' edit pencils). */
  onEditStep?: (step: BookingStep) => void;
  /** Side padding for the scrollable content; the sticky footer reuses it so its divider spans the full panel width. */
  edgeClassName?: string;
}

/**
 * The Payment step's Booking Summary — Address / Trip Details / Payment cards
 * with edit pencils, the live Price Summary, and Book Trip. A card-paid booking
 * goes through ConfirmCardModal first; invoice books directly. Success clears
 * the draft and lands on the confirmation page; failure keeps the draft intact.
 */
const AuthedPricedBookingSummary = ({
  className,
  hideHeading = false,
  activeStep,
  onEditStep,
  edgeClassName = "px-5",
}: PricedBookingSummaryProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // The OBO entry point (Booking Manager vs a client's profile/table) tags the booking route's history
  // state; forward it to the confirmation so its "Back" link returns the operator where they started.
  const bookingOrigin = (useLocation().state as { bookingOrigin?: "client-profile" } | null)?.bookingOrigin;
  const { accountId } = useClientScope();
  const { operatorId, accessId } = useParams<{ operatorId: string; accessId: string }>();
  const isOperatorScoped = !!accountId && !!operatorId;
  // EA booking on behalf of a managed client (DEV-1296) — the scoped route carries `:accessId`.
  const isAssistantScoped = !!accountId && !!accessId;
  // Read-only replay of a closed booking — the Book Trip footer is hidden.
  const viewOnly = useBookingViewOnly();

  const locations = useAppSelector(selectOrderedLocations);
  const draft = useAppSelector(selectClientBookingDraft);
  const paymentMethod = useAppSelector(selectPaymentMethod);
  const accountHolder = useAccountHolder();
  const { quote, isQuotable, isLoading, missingFields } = useBookingQuote();
  const [createBooking] = useCreateClientBookingMutation();
  const [updateBooking] = useUpdateClientBookingMutation();
  const [uploadAttachment] = useUploadClientBookingAttachmentMutation();
  const [deleteAttachment] = useDeleteClientBookingAttachmentMutation();
  const { files, removedExistingIds, clearFiles } = useBookingAttachments();
  // Summary box shows existing attachments (minus staged removals) + newly-picked files.
  const attachmentNames = useAttachmentDisplayNames();
  const [confirmCardOpen, setConfirmCardOpen] = useState(false);

  // Edit mode: the sheet was opened onto an existing job (/client/booking/:jobId/edit). The CTA
  // becomes "Update Booking", gated on there being at least one changed record; clicking opens the
  // change-review modal, which commits the PUT. `changes` doubles as the "has an update" gate.
  const editJobId = useAppSelector(selectEditJobId);
  const editOriginal = useAppSelector(selectEditOriginal);
  const isEdit = editJobId != null;
  // Existing attachments on the booking — needed to show the real From (original files) → To (resulting
  // files) in the review modal.
  const { data: existingDocs = [] } = useGetClientBookingDocumentsQuery(
    editJobId != null ? { jobId: editJobId, accountId: accountId ?? undefined } : skipToken
  );
  const changes = useMemo(() => {
    if (!isEdit) return [];
    const diff = buildBookingDiff(editOriginal, draft);
    // Attachments live in the sheet context, not the serialisable draft, so buildBookingDiff can't see
    // them. Surface a change when the user added a new file (uploaded after the PUT) or staged an
    // existing one for removal (deleted after the PUT). From = the booking's original attachments; To =
    // what's left after the edit (kept existing + newly-picked) — a real before→after, like every row.
    if (files.length || removedExistingIds.length) {
      const fromNames = existingDocs.map((doc) => doc.filename);
      const toNames = [
        ...existingDocs.filter((doc) => !removedExistingIds.includes(doc.uuid)).map((doc) => doc.filename),
        ...files.map((file) => file.name),
      ];
      diff.push({
        key: "attachments",
        label: "Attachments",
        from: fromNames.length ? fromNames.join(", ") : "None",
        to: toNames.length ? toNames.join(", ") : "None",
      });
    }
    return diff;
  }, [isEdit, editOriginal, draft, files, removedExistingIds, existingDocs]);
  const [reviewOpen, setReviewOpen] = useState(false);

  // The pickup, re-derived from the LATEST landing when the booking's flight has drifted materially. The
  // same hook the sheet root holds, so this reads the already-resolved cache entry rather than firing a
  // lookup of its own.
  const { applyRederivedPickup, revertRederivedPickup } = useSheetFlightPickup();

  /**
   * "Update Booking" — commit any flight re-derivation FIRST, then open the change-review modal.
   *
   * The order is the whole design. `changes` is a memo over the draft, so committing here is what puts
   * `Pickup Time: <old> → <new>` in the table the client confirms — a pickup that moves on save is
   * disclosed, never silent. `submitUpdate` runs from a closure rebuilt on the render this dispatch
   * causes, so `pickup_datetime` is in the payload before the server's own
   * `hasPricingRelevantFieldsChanged()` reads it and the booking reprices on its own — no second pricing
   * call from here.
   *
   * Deliberately NOT also applied inside `submitUpdate`: a re-derivation that only resolved after the
   * modal opened would then be saved without appearing in the diff the client agreed to. Applying at
   * exactly one point keeps the payload and the disclosure the same set of changes.
   */
  const openReview = () => {
    applyRederivedPickup();
    setReviewOpen(true);
  };

  /**
   * "Keep Editing" — and the modal's own dismiss, which means the same thing. Both are the client declining
   * the changes they were just shown, so the re-derivation `openReview` committed has to come back out.
   *
   * Without this the decline keeps a pickup the client refused: the CTA stays enabled on a change nobody
   * made once they revert their own edit, and `hasPricingChange` keeps the summary on a live re-quote
   * instead of the price the booking was invoiced at. That is the property the save-click fire point exists
   * to protect, so declining has to restore it exactly.
   *
   * The failed-save path deliberately does NOT revert: the client confirmed, the save is retryable, and the
   * disclosed pickup is still what the next attempt should send.
   */
  const closeReview = () => {
    revertRederivedPickup();
    setReviewOpen(false);
  };
  // Spans the WHOLE submit — the create POST AND the follow-up attachment upload — so the button stays
  // disabled/loading until we navigate away. (The mutation's own isLoading flips back to false once the
  // create resolves, which would re-enable the button mid-upload.)
  const [submitting, setSubmitting] = useState(false);
  // submitting only flips after a re-render — this ref hard-blocks a second POST from
  // clicks landing in the same frame (a duplicate here is a duplicate charge).
  const submittingRef = useRef(false);

  /**
   * Snapshot the draft into the confirmation's serialisable router state, captured BEFORE the draft
   * is cleared. Mirrors what the Booking Summary panel shows on the Payment step so the confirmation
   * boxes are identical. The passenger row mirrors the summary's PassengerRow (self → account holder
   * with "(You)"; a chosen guest → "(Guest)").
   */
  const buildConfirmationState = (): ClientBookingConfirmationState => {
    const chosen = draft.passenger;
    const isSelf = !chosen || chosen.kind === "self";
    const baseName = isSelf
      ? `${accountHolder.firstName} ${accountHolder.lastName}`.trim()
      : `${chosen.firstName} ${chosen.lastName}`.trim();

    const parsed = draft.pickupDatetime ? new Date(draft.pickupDatetime) : null;
    const isValidDate = !!parsed && !Number.isNaN(parsed.getTime());

    const extraPassengers = (draft.passengers ?? [])
      .map((p, index) => ({ ...p, index }))
      .filter((p) => p.index > 0 && p.name.trim())
      .map((p) => ({ name: p.name, phone: p.phone }));

    const trip: TripDetails = {
      passenger: {
        name: baseName ? (isSelf ? `${baseName}${accountHolder.selfSuffix}` : `${baseName} (Guest)`) : "",
        email: isSelf ? accountHolder.email : chosen?.email,
        phone: isSelf ? accountHolder.mobile : chosen?.phone,
        avatarSrc: isSelf ? accountHolder.avatar : undefined,
      },
      // Passenger 1 as a labelled row, mirroring the Booking Summary panel (raw name, no "(You)"/"(Guest)").
      mainPassenger: baseName ? { name: baseName, phone: isSelf ? accountHolder.mobile : chosen?.phone } : undefined,
      locations,
      date: isValidDate && parsed ? format(parsed, "d MMMM yyyy") : draft.pickupDatetime || "—",
      time: isValidDate && parsed ? format(parsed, "h:mm a") : undefined,
      // Mirrors the Booking Summary's date row so the confirmation can't drift from it — same helper,
      // same flight gate. No design node covers the confirmation card, so it follows Trip Details.
      delayCaption: (draft.pickupFlightNumber ? driverDelayCaption(draft.pickupFlightDelayMinutes) : null) ?? undefined,
      vehicleLabel: draft.vehicle ? draft.transferType?.label ?? "Vehicle and Transfer" : "",
      vehicleType: draft.vehicle?.name
        ? `${draft.vehicle.name} (${capacityLabel(draft.numOfPassengers ?? 1, draft.numOfBags ?? 0)})`
        : undefined,
      // Duration (hourly/daily) mirrors the Booking Summary's vehicle row; the price override (any transfer
      // type now) shows whenever the operator set one.
      duration: transferRateKind(draft.jobRate) !== "journey" ? formatDuration(draft.bookedForDuration) || undefined : undefined,
      fixedRate: draft.fixedPrice != null ? `$${draft.fixedPrice}` : undefined,
      vehicleExtras: extrasLabel(draft.extras) || undefined,
      bookedBy: draft.bookedBy || undefined,
      reference: draft.reference || undefined,
      additionalInfo: draft.additionalInfo || undefined,
      attachments: files.map((file) => file.name),
      extraPassengers,
      paymentMethod,
    };

    // Raw trip facts for a re-book (reverse/duplicate) from the confirmation — the display `trip`
    // carries formatted strings, but a re-book needs the raw vehicle/transfer/counts (see buildRebookDraft).
    const rebook: RebookSource = {
      locations,
      vehicle: draft.vehicle,
      transferType: draft.transferType ? { id: draft.transferType.id, label: draft.transferType.label } : null,
      numOfPassengers: draft.numOfPassengers,
      numOfBags: draft.numOfBags,
      additionalInfo: draft.additionalInfo,
      jobRate: draft.jobRate,
      bookedForDuration: draft.bookedForDuration,
    };

    return isQuotable && quote ? { trip, rebook, priceGroups: toGroups(quote), total: fmt(Number(quote.total)) } : { trip, rebook };
  };

  const submit = async () => {
    const payload = buildBookingPayload(draft);
    if (!payload || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    try {
      // Capture the confirmation snapshot before anything clears the draft.
      const confirmationState = buildConfirmationState();
      const created = await createBooking({ payload, accountId }).unwrap();
      // Read the job id defensively (top-level, else nested under `booking`). A missing id means we
      // must NOT upload — posting to /booking/undefined/document 404s. The booking itself succeeded,
      // so we still proceed to the confirmation; only the attachments are skipped.
      const jobId = created.job_id ?? created.booking?.job_id;
      if (jobId != null) {
        // Upload attachments to the freshly-created job. A failed upload must NOT discard the booking.
        await uploadBookingAttachments(files, (body) => uploadAttachment({ jobId, body, accountId }).unwrap());
      } else if (files.length) {
        addToast("info", "Your booking was created, but attachments could not be uploaded.");
      }
      // Booking number for the confirmation heading — the response's display id when present, else
      // derived from the numeric id (same "J######" format as the backend).
      const displayJobId = created.booking?.display_job_id ?? (jobId != null ? formatDisplayJobId(jobId) : undefined);
      setConfirmCardOpen(false);
      clearFiles();
      dispatch(clearBooking());
      // Operator OBO lands on the operator-scoped confirmation (the client-portal route is behind
      // RequireClientAccount, which would bounce a non-client account to "/"); EA OBO lands on the
      // assistant-scoped confirmation keyed on the same access link; self-service unchanged.
      const confirmationPath = isOperatorScoped
        ? `/operator/${operatorId}/clients/${accountId}/booking/confirmation`
        : isAssistantScoped
        ? `/client/book-for/${accessId}/confirmation`
        : "/client/booking/confirmation";
      navigate(confirmationPath, { state: { ...confirmationState, displayJobId, bookingOrigin } });
    } catch (error) {
      // Draft stays intact — fix the problem (e.g. pick another card) and retry.
      setConfirmCardOpen(false);
      addToast("danger", getErrorMessages(error as Parameters<typeof getErrorMessages>[0])[0]);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleBookTrip = () => {
    if (!paymentMethod) return;
    // Only card payments need the "right card?" confirmation — invoice books directly.
    if (isCreditCard(paymentMethod)) setConfirmCardOpen(true);
    else void submit();
  };

  // Commit an edit: PUT the same payload the create flow builds. Success clears the draft and returns
  // to the client home with a success alert (NOT a confirmation screen — that would read as a new
  // booking). Failure keeps the draft intact so the user can fix and retry. Mirrors `submit`'s guards.
  const submitUpdate = async () => {
    const payload = buildBookingPayload(draft);
    if (!payload || editJobId == null || submittingRef.current) return;
    // A booking-field change (anything but the synthetic "attachments" review row) vs an edit that only
    // touches attachments. Attachments live outside the diffable draft, so this split drives both whether
    // we PUT at all and how honestly we can report the result.
    const hasFieldChanges = changes.some((change) => change.key !== "attachments");
    const attachmentOps = files.length + removedExistingIds.length;
    const displayId = `J${String(editJobId).padStart(6, "0")}`;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      // Only PUT when a real field changed. An attachments-only edit doesn't need to re-save the
      // (unchanged) booking — that would be a no-op write that bumps updated_at / re-runs pricing and
      // wrongly reads as "the booking changed" when it didn't.
      if (hasFieldChanges) {
        await updateBooking({ jobId: editJobId, payload, accountId }).unwrap();
      }
      // Apply the staged attachment edits. Silent so we compose one accurate message below from the
      // failure counts (a generic best-effort toast would misreport an attachments-only edit that saved
      // nothing). Removals only ever happen here (staged in the sheet until "Update Booking"), never on toggle.
      const uploadFailed = await uploadBookingAttachments(
        files,
        (body) => uploadAttachment({ jobId: editJobId, body, accountId }).unwrap(),
        "updated",
        { silent: true }
      );
      let deleteFailed = 0;
      if (removedExistingIds.length) {
        const results = await Promise.allSettled(
          removedExistingIds.map((uuid) => deleteAttachment({ jobId: editJobId, uuid, accountId }).unwrap())
        );
        deleteFailed = results.filter((r) => r.status === "rejected").length;
      }
      const attachmentFailed = uploadFailed + deleteFailed;

      // Attachments-only edit whose attachment work ENTIRELY failed → nothing the user wanted was saved.
      // Don't claim success or navigate away; keep the draft + staged files so they can retry (the CTA
      // stays enabled). This is the case the tester hit (upload 500 on the missing DB column).
      if (!hasFieldChanges && attachmentOps > 0 && attachmentFailed >= attachmentOps) {
        setReviewOpen(false);
        addToast("danger", "Your attachment couldn't be saved, so no changes were made to your booking. Please try again.");
        return;
      }

      setReviewOpen(false);
      clearFiles();
      dispatch(clearBooking());
      if (attachmentFailed > 0) {
        // Real field changes saved, but the attachment didn't (the picker only ever stages one file).
        addToast("info", `Booking ${displayId} was updated, but the attachment couldn't be saved.`);
      } else {
        addToast("success", `Booking ${displayId} has been updated`);
      }
      // Tag the nav so the LeaveBookingModal blocker doesn't intercept this happy-path exit (the draft
      // was just cleared, but the blocker's closure still sees it this tick). Operator OBO returns to the
      // client's record (Details tab = their Booking History) — NOT a fresh blank booking sheet; EA OBO
      // returns to its My Clients hub; self-service to the portal home.
      const afterUpdatePath = isOperatorScoped
        ? `/operator/${operatorId}/clients/${accountId}/details`
        : isAssistantScoped
        ? "/client/my-clients"
        : "/client";
      navigate(afterUpdatePath, { state: { skipBookingLeaveGuard: true } });
    } catch (error) {
      setReviewOpen(false);
      addToast("danger", getErrorMessages(error as Parameters<typeof getErrorMessages>[0])[0]);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className={clsx("flex min-h-full w-full flex-col", className)}>
      <div className={clsx("flex flex-col", edgeClassName)}>
        {!hideHeading && (
          <Typography variant="title" className="mb-4 block">
            Booking Summary
          </Typography>
        )}

        <ScopedClientCard />

        <div className="flex flex-col gap-2">
          {/* The active step's card hides its pencil — you're already editing that step. */}
          <AddressSummaryCard
            locations={locations}
            pickupFlightNumber={draft.pickupFlightNumber}
            highlighted={activeStep === "address"}
            onEdit={onEditStep && activeStep !== "address" ? () => onEditStep("address") : undefined}
          />
          <TripDetailsSummaryCard
            bookedBy={draft.bookedBy}
            reference={draft.reference}
            additionalInfo={draft.additionalInfo}
            passengers={draft.passengers}
            attachments={attachmentNames}
            highlighted={activeStep === "trip-details"}
            onEdit={onEditStep && activeStep !== "trip-details" ? () => onEditStep("trip-details") : undefined}
          />
          <PaymentSummaryCard
            method={paymentMethod}
            highlighted={activeStep === "payment"}
            onEdit={onEditStep && activeStep !== "payment" ? () => onEditStep("payment") : undefined}
          />
        </div>

        <QuotePriceSummary
          className="mt-4"
          quote={quote}
          isQuotable={isQuotable}
          isLoading={isLoading}
          missingFields={missingFields}
          // Operator OBO only (and never in read-only replay): the Journey Cost override control.
          canOverridePrice={isOperatorScoped && !viewOnly}
        />

        {/* mb-4 keeps ≥16px between the trust note and the sticky footer's divider when scrolled. */}
        <TrustNote className="mb-4 mt-4" />
      </div>

      {/* Pinned to the bottom of the scroll container (sidebar on lg+, slide-in below) while the summary
          scrolls behind it — per Figma 5546-151643. Full-bleed: reuses edgeClassName (20px sides) so the
          divider spans the panel's full width; 16px vertical padding. Hidden in view-only (a closed
          booking can't be re-booked from here). */}
      {!viewOnly && (
        <div className={clsx("sticky bottom-0 mt-auto flex flex-col gap-3 border-t border-neutral-mid-gray bg-white py-4", edgeClassName)}>
          {isEdit ? (
            <>
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                // "Update Booking" only enables once there's an actual change to save.
                disabled={!isQuotable || !paymentMethod || changes.length === 0 || submitting}
                onClick={openReview}
                startIcon={submitting && !reviewOpen ? <Spinner variant="light" /> : undefined}
              >
                Update Booking
              </Button>
              <Typography variant="paragraph" className="text-center">
                {changes.length === 0
                  ? "Make a change to update this booking"
                  : "Everyone involved in this booking may be notified of the updates"}
              </Typography>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                disabled={!isQuotable || !paymentMethod || submitting}
                onClick={handleBookTrip}
                startIcon={submitting && !confirmCardOpen ? <Spinner variant="light" /> : undefined}
              >
                Book Trip
              </Button>
              <Typography variant="paragraph" className="text-center">
                You can schedule a return or other trip once your booking is confirmed
              </Typography>
            </>
          )}
        </div>
      )}

      {isEdit && (
        <BookingUpdateReviewModal
          open={reviewOpen}
          changes={changes}
          onClose={closeReview}
          onConfirm={() => void submitUpdate()}
          loading={submitting}
        />
      )}

      {!isEdit && paymentMethod && isCreditCard(paymentMethod) && (
        <ConfirmCardModal
          open={confirmCardOpen}
          paymentMethod={paymentMethod}
          onClose={() => setConfirmCardOpen(false)}
          onConfirm={() => void submit()}
          loading={submitting}
        />
      )}
    </div>
  );
};

/**
 * Payment-step Booking Summary dispatcher. The authed path (`AuthedPricedBookingSummary`) is
 * unchanged — priced quote + saved-card submit to /v2/cust/booking. The guest flow
 * (`useGuestBooking().isGuest`) swaps in `GuestPricedBookingSummary`, which posts to the guest
 * booking-create endpoint with a tokenised card + reCAPTCHA. Each variant is its own component so
 * hook order stays unconditional.
 */
export const PricedBookingSummary = (props: PricedBookingSummaryProps) => {
  const { isGuest } = useGuestBooking();
  return isGuest ? <GuestPricedBookingSummary {...props} /> : <AuthedPricedBookingSummary {...props} />;
};
